/**
 * A selector for students in a class
 **/
import {plural, t} from "@lingui/core/macro";
import {useEffect, useState} from "react";

import {
    MultiSelect,
    OptionItem,
    type LabelsValues,
} from "@khanacademy/wonder-blocks-dropdown";

import ApolloQuery from "~/data-access/gql/apollo-query.tsx";

import {STUDENT_SELECTOR_QUERY as DATA_QUERY} from "./queries.ts";
import type {StudentKaidAndNickname} from "./types.ts";

interface StudentWithAIGuideEnabledStatus {
    kaid: string;
    coachNickname: string;
    isAIGuideEnabled: boolean;
}

interface StudentMultiSelectProps {
    /**
     * Whether the dropdown should be disabled.
     */
    disabled: boolean;
    /**
     * An optional width for the dropdown component.
     */
    dropdownWidth?: number;
    /**
     * The collection from which we will populate the dropdown options.
     */
    items: Array<StudentWithAIGuideEnabledStatus>;
    /**
     * Whether the dropdown data is being loaded.
     */
    loading: boolean;
    /**
     * Called when the selection has changed.
     */
    onChange: (student: Array<string>) => void;
    /**
     * The selected students in the dropdown.
     */
    selectedValues: Array<string>;
    /**
     * Used when multi classes selecting is available to modify the dropdown
     * placeholder.
     */
    totalStudentCount?: number;
    /**
     * Whether any classes are selected. Used when multi classes selecting is available.
     */
    classSelected?: boolean;
    /**
     * Whether students who can be selected must be AIGuide-enabled.
     */
    aiGuideEnabledStudentsOnly?: boolean;
    /**
     * Optional id to pair a MultiSelect with a label element.
     */
    id?: string;
}

/**
 * Default width (in case we don't provide one via props)
 */
const SELECTOR_WIDTH = 244;

/**
 * A MultiSelect wrapper to list and select the students related to a given
 * class.
 */
const StudentMultiSelect = (props: StudentMultiSelectProps) => {
    const {
        disabled = false,
        dropdownWidth,
        items,
        loading,
        totalStudentCount,
        classSelected,
        aiGuideEnabledStudentsOnly,
        id,
        selectedValues: propSelectedValues = [],
        onChange,
    } = props;

    const finalDropdownWidth = dropdownWidth ?? SELECTOR_WIDTH;

    const [selectedValues, setSelectedValues] =
        useState<Array<string>>(propSelectedValues);

    useEffect(() => {
        setSelectedValues(propSelectedValues);
    }, [propSelectedValues]);

    const selectedLength = selectedValues.length;
    const studentLength = items.length;

    let noneSelectedCustomLabel = t`No students`;

    if (loading) {
        noneSelectedCustomLabel = t`Loading...`;
    } else if (!classSelected) {
        // In case we don't select any class at all.
        noneSelectedCustomLabel = t`No students`;
    } else if (totalStudentCount === 0) {
        // In case we select a class, but haven't added any students yet.
        noneSelectedCustomLabel = t`All future students`;
    } else if (studentLength === selectedLength) {
        // In case we select all the students for all the classes, we change
        // the label even if the MultiSelect don't have any selectedValues.
        noneSelectedCustomLabel = t`All students`;
    }

    // Custom MultiSelect values
    // NOTE: We modify the combobox values dynamically depending if the
    // assignment can select between multiple classes.
    const dropdownLabels: Partial<LabelsValues> = {
        selectNoneLabel: t`Select no students`,
        selectAllLabel: (numStudents: number) =>
            plural(numStudents, {
                one: "Select # student ",
                other: "Select all # students ",
            }),
        noneSelected: noneSelectedCustomLabel,
        someSelected: (numSelectedValues: number) =>
            plural(numSelectedValues, {
                one: "# student",
                other: "# students",
            }),
        allSelected: t`All students`,
    };

    const options = items.map((student) => {
        return (
            <OptionItem
                label={student.coachNickname}
                value={student.kaid}
                key={student.kaid}
                testId="assignment-student-option"
                disabled={
                    aiGuideEnabledStudentsOnly && !student.isAIGuideEnabled
                }
            />
        );
    });

    const handleChange = (newSelectedValues: Array<string>) => {
        setSelectedValues(newSelectedValues);
        onChange(newSelectedValues);
    };

    return (
        <MultiSelect
            onChange={handleChange}
            labels={
                dropdownLabels as TSFixMe<"TODO(WB-1598): Update `labels` to be partial">
            }
            selectedValues={selectedValues}
            shortcuts={true}
            disabled={disabled || loading}
            style={{width: finalDropdownWidth}}
            id={id}
            testId="assignment-student-selector"
        >
            {options}
        </MultiSelect>
    );
};

interface Props {
    // The descriptor of the classroom from which students will be selected.
    // Passing an empty classDescriptor will cause the query to be skipped as
    // it is required variable and it wouldn't make sense to try finding a
    // Classroom with an empty descriptor.
    classDescriptor: string;
    // Optional students that can be passed to skip the query and use it as
    // the data.
    students?: Array<StudentKaidAndNickname>;
    // Selection state management
    onChange: (student: Array<string>) => void;
    selectedStudentIds?: Array<string>;
    // When multi classes selecting is available, we provide this.
    totalStudentCount?: number;
    // Whether any classes are selected. Used when multi classes selecting is available.
    classSelected?: boolean;
    dropdownWidth?: number;
    // Whether the dropdown should only allow students with AIGuide enabled to be selected.
    aiGuideEnabledStudentsOnly?: boolean;
    // On render should the dropdown default to all students selected. This option overrides the passed in selectedStudentIds. This option gets set to false when a user makes a selection
    startWithAllStudentsSelected?: boolean;
    /**
     * Optional id to pair a MultiSelect with a label element.
     */
    id?: string;
}

export default function StudentSelector(props: Props) {
    const [startWithAllStudentsSelected, setStartWithAllStudentsSelected] =
        useState(false);
    const {
        aiGuideEnabledStudentsOnly,
        dropdownWidth,
        classDescriptor,
        students,
        totalStudentCount,
        classSelected,
        id,
    } = props;

    useEffect(() => {
        setStartWithAllStudentsSelected(
            props.startWithAllStudentsSelected ?? false,
        );
    }, [props.startWithAllStudentsSelected]);

    const handleChange = (studentIds: string[]) => {
        setStartWithAllStudentsSelected(false);
        props.onChange(studentIds);
    };

    return (
        <ApolloQuery
            query={DATA_QUERY}
            fetchPolicy="cache-first"
            variables={{
                classDescriptor,
                aiGuideEnabledStudentsOnly: !!aiGuideEnabledStudentsOnly,
            }}
            skip={classDescriptor === "" || !!students}
        >
            {({data, loading}) => {
                const studentKaidsAndNicknames =
                    students ?? data?.classroom?.studentKaidsAndNicknames ?? [];
                const studentsWithAIGuideEnabledStatus =
                    studentKaidsAndNicknames.map((student) => {
                        const found = data?.classroom?.students?.find(
                            (s) => s.kaid === student.kaid,
                        );
                        return {
                            ...student,
                            isAIGuideEnabled:
                                !!found?.isAIGuideEnabled &&
                                !!found?.hasAccessToAIGuideLearner,
                        };
                    });

                let selectedStudentIds = props.selectedStudentIds ?? [];
                if (startWithAllStudentsSelected) {
                    selectedStudentIds = studentKaidsAndNicknames
                        .filter((student) => student != null)
                        .map(({kaid}) => kaid);
                }

                return (
                    <StudentMultiSelect
                        id={id}
                        disabled={loading}
                        dropdownWidth={dropdownWidth}
                        items={studentsWithAIGuideEnabledStatus}
                        loading={loading}
                        onChange={handleChange}
                        selectedValues={selectedStudentIds}
                        classSelected={classSelected}
                        totalStudentCount={totalStudentCount}
                        aiGuideEnabledStudentsOnly={aiGuideEnabledStudentsOnly}
                    />
                );
            }}
        </ApolloQuery>
    );
}
